import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CalendarOptions, EventClickArg, EventInput } from '@fullcalendar/core';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import { FullCalendarModule } from '@fullcalendar/angular';
import { CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Subscription } from 'rxjs';
import { Orcamento, LeadContatoPlanejado } from '../../core/models/workflow.models';
import { OrcamentosService } from '../../core/services/orcamentos.service';
import { CrmService } from '../../core/services/crm.service';
import { LeadContatoModalComponent, LeadContatoPayload } from '../../shared/components/lead-contato-modal.component';

@Component({
  selector: 'app-crm-orcamentos',
  standalone: true,
  imports: [CommonModule, FormsModule, FullCalendarModule, LeadContatoModalComponent, DragDropModule],
  templateUrl: './crm-orcamentos.component.html',
  styleUrl: './crm-orcamentos.component.scss',
})
export class CrmOrcamentosComponent implements OnInit, OnDestroy {
  orcamentos: Orcamento[] = [];
  contatos: LeadContatoPlanejado[] = [];
  filteredOrcamentos: Orcamento[] = [];
  selectedOrcamento?: Orcamento;
  filterPlaca = '';
  filterStatus:
    | 'TODOS'
    | 'Aberto'
    | 'Aguardando retorno'
    | 'Efetuar Contato'
    | 'Perdido'
    | 'Ganho' = 'TODOS';
  activeTab: 'board' | 'calendar' = 'board';
  readonly statusColumns = [
    'Aberto',
    'Aguardando retorno',
    'Efetuar Contato',
    'Perdido',
    'Ganho',
  ] as const;
  private groupedOrcamentos: Record<string, Orcamento[]> = {};

  showCreateModal = false;
  showEventModal = false;
  selectedEvent?: LeadContatoPlanejado;
  modalAlert = '';

  leadModalOpen = false;
  selectedLead: Orcamento | null = null;
  leadContacts: Array<{ leadId: number | string; tipo: string; obs: string; dataHora: string }> = [];

  novoOrcamentoOpen = false;
  novoOrcamentoError = '';
  novoOrcamento = this.buildOrcamentoForm();
  consultores = ['Carla Mendes', 'Marcos Silva'];

  newContato = {
    canal: 'WHATSAPP',
    assunto: '',
    observacao: '',
    dataHora: new Date(),
  };

  calendarEvents: EventInput[] = [];

  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    initialView: 'dayGridMonth',
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth,timeGridWeek,timeGridDay',
    },
    eventDisplay: 'block',
    displayEventTime: false,
    eventContent: (arg) => {
      const props = (arg.event.extendedProps || {}) as {
        status?: string;
        contatoTipo?: string;
        canal?: string;
        placa?: string;
      };
      const status = String(props.status || 'LEAD').toUpperCase();
      const placa = props.placa || arg.event.title || '';
      const tipo = String(props.contatoTipo || props.canal || 'WHATSAPP').toUpperCase();

      return {
        html: `
          <div class="crm-evt crm-evt--${status} crm-evt-tipo--${tipo}">
            <span class="crm-evt__text">${placa}</span>
          </div>
        `,
      };
    },
    dateClick: (arg) => this.handleDateClick(arg),
    eventClick: (info) => {
      info.jsEvent.preventDefault();
      const props = (info.event.extendedProps || {}) as {
        canal?: LeadContatoPlanejado['canal'];
        contatoTipo?: LeadContatoPlanejado['canal'];
        assunto?: string;
        observacao?: string;
        dataHora?: Date;
      };
      const contatoId = Number(info.event.id);
      const found = this.contatos.find((contato) => contato.id === contatoId);
      this.selectedEvent =
        found ??
        ({
          id: contatoId,
          orcamentoId: 0,
          dataHora: props.dataHora ?? info.event.start ?? new Date(),
          canal: (props.canal ?? props.contatoTipo ?? 'WHATSAPP') as LeadContatoPlanejado['canal'],
          assunto: props.assunto ?? info.event.title ?? '',
          observacao: props.observacao,
          concluido: false,
        } as LeadContatoPlanejado);
      this.showEventModal = true;
    },
    height: 'auto',
  };

  private subscription = new Subscription();

  constructor(private orcamentosService: OrcamentosService, private crmService: CrmService) {}

  ngOnInit(): void {
    this.subscription.add(
      this.orcamentosService.getAll().subscribe((orcamentos) => {
        this.orcamentos = orcamentos;
        this.applyFilters();
        this.calendarEvents = this.mapEvents();
      })
    );
    this.subscription.add(
      this.crmService.getAll().subscribe((contatos) => {
        this.contatos = contatos;
        this.calendarEvents = this.mapEvents();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  applyFilters(): void {
    const placaFiltro = this.filterPlaca.trim().toLowerCase();
    this.filteredOrcamentos = this.orcamentos.filter((orcamento) => {
      const matchPlaca = !placaFiltro || orcamento.placa.toLowerCase().includes(placaFiltro);
      const statusAtendimento = (orcamento as Orcamento & { statusAtendimento?: string })
        .statusAtendimento;
      const matchStatus =
        this.filterStatus === 'TODOS' ? true : statusAtendimento === this.filterStatus;
      return matchPlaca && matchStatus;
    });
    this.rebuildGroups();
  }

  selectOrcamento(orcamento: Orcamento): void {
    this.selectedOrcamento = orcamento;
    this.modalAlert = '';
  }

  openLeadModal(lead: Orcamento): void {
    this.selectedLead = lead;
    this.selectedOrcamento = lead;
    this.leadModalOpen = true;
  }

  setActiveTab(tab: 'board' | 'calendar'): void {
    this.activeTab = tab;
  }

  closeLeadModal(): void {
    this.leadModalOpen = false;
    this.selectedLead = null;
  }

  onLeadSalvar(payload: LeadContatoPayload): void {
    const registro = {
      leadId: payload.leadId,
      tipo: payload.tipo,
      obs: payload.obs,
      dataHora: new Date().toISOString(),
    };
    this.leadContacts = [...this.leadContacts, registro];
    console.log('Contato salvo', registro);
    this.closeLeadModal();
  }

  onLeadEncerrar(): void {
    this.closeLeadModal();
  }

  openNovoOrcamento(): void {
    this.novoOrcamento = this.buildOrcamentoForm();
    this.novoOrcamentoError = '';
    this.novoOrcamentoOpen = true;
  }

  closeNovoOrcamento(): void {
    this.novoOrcamentoOpen = false;
  }

  saveNovoOrcamento(): void {
    const errors = this.validateNovoOrcamento();
    if (errors.length) {
      this.novoOrcamentoError = errors.join(' ');
      return;
    }

    const novo: Orcamento = {
      id: 0,
      placa: this.novoOrcamento.placa.trim(),
      marca: this.novoOrcamento.marca.trim(),
      modelo: this.novoOrcamento.modelo.trim(),
      valor: Number(this.novoOrcamento.valor),
      consultorResponsavel: this.novoOrcamento.consultorResponsavel,
      status: 'LEAD',
      clienteNome: this.novoOrcamento.clienteNome.trim(),
      clienteTelefone: this.novoOrcamento.clienteTelefone.trim(),
      clienteEmail: this.novoOrcamento.clienteEmail.trim(),
      criadoEm: new Date(),
      valorOrcamento: Number(this.novoOrcamento.valor),
      consultor: this.novoOrcamento.consultorResponsavel,
      statusLead: 'ABERTO',
      dataCriacao: new Date(),
      dataFechamento: null,
      virouOs: false,
    };

    this.orcamentosService.addOrcamento(novo);
    this.novoOrcamentoOpen = false;
  }

  handleDateClick(arg: { date: Date }): void {
    if (!this.selectedOrcamento) {
      this.modalAlert = 'Selecione um orcamento ou lead antes de criar um contato.';
      return;
    }
    this.modalAlert = '';
    this.newContato = {
      canal: 'WHATSAPP',
      assunto: '',
      observacao: '',
      dataHora: arg.date,
    };
    this.showCreateModal = true;
  }

  handleEventClick(arg: EventClickArg): void {
    const contatoId = Number(arg.event.id);
    this.selectedEvent = this.contatos.find((contato) => contato.id === contatoId);
    this.showEventModal = true;
  }

  criarContato(): void {
    if (!this.selectedOrcamento) {
      return;
    }
    this.crmService.addContatoPlanejado({
      orcamentoId: this.selectedOrcamento.id,
      dataHora: this.newContato.dataHora,
      canal: this.newContato.canal as LeadContatoPlanejado['canal'],
      assunto: this.newContato.assunto,
      observacao: this.newContato.observacao,
    });
    this.showCreateModal = false;
  }

  marcarConcluido(): void {
    if (this.selectedEvent) {
      this.crmService.marcarContatoConcluido(this.selectedEvent.id);
      this.showEventModal = false;
    }
  }

  closeModals(): void {
    this.showCreateModal = false;
    this.showEventModal = false;
  }

  onDateInputChange(value: string): void {
    if (value) {
      this.newContato.dataHora = new Date(value);
    }
  }

  private mapEvents(): EventInput[] {
    const orcamentosById = new Map(this.orcamentos.map((item) => [item.id, item]));
    return this.contatos.map((contato) => {
      const orcamento = orcamentosById.get(contato.orcamentoId);
      return {
        id: String(contato.id),
        title: orcamento?.placa ?? 'Contato',
        start: contato.dataHora,
        backgroundColor: contato.concluido ? '#cbd5f5' : '#3f8efc',
        borderColor: contato.concluido ? '#cbd5f5' : '#2f73e6',
        extendedProps: {
          status: orcamento?.status ?? 'LEAD',
          contatoTipo: contato.canal ?? 'WHATSAPP',
          canal: contato.canal ?? 'WHATSAPP',
          placa: orcamento?.placa ?? '',
          cliente: orcamento?.clienteNome ?? '',
          consultor: orcamento?.consultorResponsavel ?? '',
          assunto: contato.assunto ?? '',
          observacao: contato.observacao ?? '',
          dataHora: contato.dataHora,
        },
      };
    });
  }

  private buildOrcamentoForm() {
    return {
      placa: '',
      marca: '',
      modelo: '',
      clienteNome: '',
      clienteTelefone: '',
      clienteEmail: '',
      consultorResponsavel: 'Carla Mendes',
      valor: 0,
    };
  }

  private validateNovoOrcamento(): string[] {
    const errors: string[] = [];
    if (!this.novoOrcamento.placa.trim()) errors.push('Placa obrigatoria.');
    if (!this.novoOrcamento.marca.trim()) errors.push('Marca obrigatoria.');
    if (!this.novoOrcamento.modelo.trim()) errors.push('Modelo obrigatorio.');
    if (!this.novoOrcamento.clienteNome.trim()) errors.push('Cliente obrigatorio.');
    if (!this.novoOrcamento.consultorResponsavel) errors.push('Consultor obrigatorio.');
    if (Number(this.novoOrcamento.valor) <= 0) errors.push('Valor invalido.');
    return errors;
  }

  dropListId(status: (typeof this.statusColumns)[number]): string {
    return `drop-${status.toLowerCase().replace(/\s+/g, '-')}`;
  }

  get connectedDropListsIds(): string[] {
    return this.statusColumns.map((status) => this.dropListId(status));
  }

  getGroupedOrcamentos(status: (typeof this.statusColumns)[number]): Orcamento[] {
    return this.groupedOrcamentos[status] ?? [];
  }

  onDrop(event: CdkDragDrop<Orcamento[]>, status: (typeof this.statusColumns)[number]): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      const moved = event.container.data[event.currentIndex];
      (moved as Orcamento & { statusAtendimento?: string }).statusAtendimento = status;
    }
    this.persistOrcamentos();
  }

  private rebuildGroups(): void {
    this.groupedOrcamentos = this.statusColumns.reduce<Record<string, Orcamento[]>>((acc, status) => {
      acc[status] = this.filteredOrcamentos.filter((orcamento) => {
        const statusAtendimento = (orcamento as Orcamento & { statusAtendimento?: string })
          .statusAtendimento;
        return statusAtendimento === status;
      });
      return acc;
    }, {});
  }

  private persistOrcamentos(): void {
    const byId = new Map(this.orcamentos.map((item) => [item.id, item]));
    const used = new Set<number>();
    const ordered: Orcamento[] = [];

    this.statusColumns.forEach((status) => {
      const group = this.groupedOrcamentos[status] ?? [];
      group.forEach((item) => {
        const current = byId.get(item.id);
        if (!current || used.has(current.id)) {
          return;
        }
        const statusAtendimento = (current as Orcamento & { statusAtendimento?: string })
          .statusAtendimento;
        if (statusAtendimento !== status) {
          (current as Orcamento & { statusAtendimento?: string }).statusAtendimento = status;
        }
        ordered.push(current);
        used.add(current.id);
      });
    });

    this.orcamentos.forEach((item) => {
      if (!used.has(item.id)) {
        ordered.push(item);
      }
    });

    this.orcamentos = ordered;
    this.orcamentosService.setAll(ordered);
    this.applyFilters();
  }

  getOrcamentosByStatus(status: (typeof this.statusColumns)[number]): Orcamento[] {
    return this.filteredOrcamentos.filter((orcamento) => {
      const statusAtendimento = (orcamento as Orcamento & { statusAtendimento?: string })
        .statusAtendimento;
      return statusAtendimento === status;
    });
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Aberto':
        return 'st-aberto';
      case 'Aguardando retorno':
        return 'st-aguardando';
      case 'Efetuar Contato':
        return 'st-efetuar';
      case 'Perdido':
        return 'st-perdido';
      case 'Ganho':
        return 'st-ganho';
      default:
        return 'st-default';
    }
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'Aberto':
        return '#2563eb';
      case 'Aguardando retorno':
        return '#7c3aed';
      case 'Efetuar Contato':
        return '#f59e0b';
      case 'Perdido':
        return '#ef4444';
      case 'Ganho':
        return '#22c55e';
      default:
        return '#64748b';
    }
  }

  formatNumeroOrcamento(orcamento: Orcamento): string {
    const candidate =
      (orcamento as Orcamento & { numeroOrcamento?: string | number }).numeroOrcamento ??
      (orcamento as Orcamento & { numero?: string | number }).numero ??
      (orcamento as Orcamento & { codigo?: string | number }).codigo ??
      (orcamento as Orcamento & { codigoExterno?: string | number }).codigoExterno;

    if (candidate !== undefined && candidate !== null && String(candidate).trim()) {
      return String(candidate);
    }

    return `ORC-${String(orcamento.id).padStart(6, '0')}`;
  }

  getVeiculoLabel(orcamento: Orcamento): string {
    const placa = orcamento.placa || '';
    const marca = orcamento.marca || '';
    const modelo = orcamento.modelo || '';
    const detalhes = [marca, modelo].filter(Boolean).join(' ');
    return detalhes ? `${placa} - ${detalhes}` : placa;
  }

  getValor(orcamento: Orcamento): number {
    const valor =
      (orcamento as Orcamento & { valorOrcamento?: number }).valorOrcamento ??
      (orcamento as Orcamento & { valor?: number }).valor ??
      (orcamento as Orcamento & { total?: number }).total ??
      0;
    return Number(valor) || 0;
  }

  getConsultorNome(orcamento: Orcamento): string {
    const direct =
      (orcamento as Orcamento & { consultorResponsavel?: string }).consultorResponsavel ??
      (orcamento as Orcamento & { consultor?: string }).consultor ??
      (orcamento as Orcamento & { responsavelAtual?: string }).responsavelAtual ??
      (orcamento as Orcamento & { consultorNome?: string }).consultorNome;

    if (direct && String(direct).trim()) {
      return String(direct).trim();
    }

    const nested = (orcamento as Orcamento & { consultor?: { nome?: string } }).consultor?.nome;
    if (nested && nested.trim()) {
      return nested.trim();
    }

    return '—';
  }

  getDataAbertura(orcamento: Orcamento): Date {
    const raw =
      (orcamento as Orcamento & { dataCriacao?: Date | string }).dataCriacao ?? orcamento.criadoEm;
    return raw instanceof Date ? raw : new Date(raw);
  }

  getDataProximoEvento(orcamento: Orcamento): Date {
    const abertura = this.getDataAbertura(orcamento);
    const next = new Date(abertura);
    next.setDate(next.getDate() + 1);
    return next;
  }
}
